import { featureFlags } from '../config/featureFlags';
import type { EffectiveStandardAccess } from '../services/standardAccessApi';
import { normalizeComplianceStandardCode } from './complianceStandard';
import { collapseStandardsForUx } from './standardCanonicalUx';

/** When policy v1 is off, consumers keep legacy catalog / questionnaire behavior. */
export function isStandardAccessPolicyV1Enabled(): boolean {
  return featureFlags.STANDARD_ACCESS_POLICY_V1;
}

/**
 * Prefer granted codes from effective access; fall back to legacy list when policy is off or empty.
 */
export function pickGrantedStandardCodes(
  effective: EffectiveStandardAccess | null | undefined,
  legacyCodes: string[],
): string[] {
  if (!isStandardAccessPolicyV1Enabled()) {
    return legacyCodes;
  }
  const granted = effective?.standardCodes ?? [];
  if (granted.length > 0) {
    return granted;
  }
  return legacyCodes;
}

export function isStandardGrantedInEffective(
  effective: EffectiveStandardAccess | null | undefined,
  standardCode: string,
): boolean {
  if (!isStandardAccessPolicyV1Enabled()) {
    return true;
  }
  if (!effective?.standardCodes?.length) {
    return true;
  }
  const target = normalizeComplianceStandardCode(standardCode);
  return effective.standardCodes.some(
    (c) => normalizeComplianceStandardCode(c) === target,
  );
}

export const NO_STANDARDS_GRANTED_MESSAGE_KEY = 'standardGovernance.noStandardsGranted';

/**
 * Standards visible in selectors and workflow lists: org-enabled (starter/registry)
 * intersected with explicit org/user grants when any grants exist.
 * When userScopedStandards is provided, only those codes may appear (Starter Guide RBAC).
 */
export function resolveVisibleStandardCodes(
  enabledStandards: string[],
  effective: EffectiveStandardAccess | null | undefined,
  userScopedStandards?: string[] | null,
): string[] {
  const enabled = Array.from(
    new Set(
      (enabledStandards || [])
        .map((code) => normalizeComplianceStandardCode(code))
        .filter((code) => code && code !== 'ALL'),
    ),
  );
  const userScope = Array.from(
    new Set(
      (userScopedStandards || [])
        .map((code) => normalizeComplianceStandardCode(code))
        .filter((code) => code && code !== 'ALL'),
    ),
  );
  let visible = enabled;
  if (userScope.length > 0) {
    const scopeSet = new Set(userScope);
    visible = visible.filter((code) => scopeSet.has(code));
  }
  if (!effective?.standardCodes?.length) {
    return visible;
  }
  const granted = new Set(
    effective.standardCodes.map((code) => normalizeComplianceStandardCode(code)),
  );
  return visible.filter((code) => granted.has(code));
}

export function buildStandardsSelectorOptions(
  enabledStandards: string[],
  availableStandards: Array<{ code: string; name?: string | null }>,
  effective: EffectiveStandardAccess | null | undefined,
  userScopedStandards?: string[] | null,
): Array<{ code: string; name: string }> {
  const nameByCode = new Map(
    availableStandards.map((item) => [
      normalizeComplianceStandardCode(item.code),
      item.name || item.code,
    ]),
  );

  const visible = resolveVisibleStandardCodes(enabledStandards, effective, userScopedStandards);
  return collapseStandardsForUx(
    visible.map((code) => ({
      code,
      name: nameByCode.get(code) || code,
    })),
    { nis2Label: 'NIS2' },
  ).map((item) => ({
    code: item.code!,
    name: item.name || item.code!,
  }));
}
